import { useEffect } from "react";
import { Link } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { Printer, ArrowLeft } from "lucide-react";
import { LOGO_URL } from "@/constants/brand";

const Toolbar = () => (
  <div className="max-w-[210mm] mx-auto mb-6 flex justify-between items-center px-2 print:hidden">
    <Link to="/" data-testid="poster-back" className="flex items-center gap-2 text-sm text-cocoa-800 hover:text-terracotta font-sans">
      <ArrowLeft size={15} /> Back to website
    </Link>
    <button onClick={() => window.print()} data-testid="poster-print-btn" className="flex items-center gap-2 bg-cocoa-900 text-parchment-100 px-6 py-3 text-sm font-sans hover:bg-terracotta transition-colors">
      <Printer size={16} /> Print poster
    </button>
  </div>
);

export default function PrintPoster({ docTitle, kicker, title, intro, qrValue, urlLabel, qrSize = 215, steps, children, footerNote, testId }) {
  useEffect(() => {
    document.title = `${docTitle} · Saint Francis of Assisi Parish`;
  }, [docTitle]);

  return (
    <div className="min-h-screen bg-parchment-200 py-10 print:p-0 print:bg-white">
      <style>{`@page { size: A4 portrait; margin: 0; } @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }`}</style>
      <Toolbar />
      <article data-testid={testId} className="relative mx-auto w-[210mm] h-[297mm] bg-parchment-50 shadow-2xl print:shadow-none overflow-hidden flex flex-col">
        <div className="absolute inset-[9mm] border border-cocoa-900/40 pointer-events-none" />
        <div className="absolute inset-[11mm] border border-gold/70 pointer-events-none" />

        <header className="pt-[18mm] px-[24mm] text-center">
          <img src={LOGO_URL} alt="Parish seal" className="w-[24mm] h-[24mm] mx-auto rounded-full border border-cocoa-900/30 object-cover" />
          <div className="mt-3 text-[10pt] uppercase tracking-[0.3em] text-cocoa-700 font-sans">Saint Francis of Assisi Parish</div>
          <div className="text-[9pt] text-cocoa-700/80 font-sans">Barangay Mabuhay · General Santos City</div>
        </header>

        <div className="px-[22mm] mt-[6mm] text-center">
          <div className="text-[11pt] uppercase tracking-[0.35em] text-terracotta font-sans">{kicker}</div>
          <h1 className="mt-2 font-serif text-[32pt] leading-[1.05] text-cocoa-900">{title}</h1>
          <p className="mt-3 font-sans text-[12pt] text-cocoa-800 leading-relaxed max-w-[150mm] mx-auto">{intro}</p>
        </div>

        <div className="mt-[7mm] flex flex-col items-center">
          <div className="p-[5mm] bg-white border-2 border-cocoa-900">
            <QRCodeSVG value={qrValue} size={qrSize} level="M" fgColor="#3E2723" />
          </div>
          <div className="mt-3 font-serif text-[16pt] text-cocoa-900 tracking-wide" data-testid="poster-url">{urlLabel}</div>
        </div>

        <ol className="mt-[7mm] px-[28mm] space-y-2">
          {steps.map((s, i) => (
            <li key={s} className="flex gap-4 items-baseline font-sans text-[11.5pt] text-cocoa-800">
              <span className="font-serif text-[16pt] text-terracotta w-6 shrink-0">{i + 1}</span> {s}
            </li>
          ))}
        </ol>

        {children}

        <footer className="mt-auto pt-[5mm] pb-[17mm] px-[24mm] text-center">
          <div className="font-serif italic text-[12pt] text-cocoa-700">“Lord, make me an instrument of your peace.”</div>
          <div className="mt-2 text-[9pt] uppercase tracking-[0.25em] text-cocoa-700/80 font-sans">{footerNote}</div>
        </footer>
      </article>
    </div>
  );
}
